/* Ketcher.jsx
 *
 * A react component for Ketcher
 *
 * Works with either Ketcher generation, detecting which at runtime. Both are served from
 * `ketcher/`, so which one an environment runs is a deployment decision. BP2-897.
 *
 * Modes (props), all on either generation:
 *   (default)     a compound.
 *   reactionMode  a reaction, as RXN; read with getReactionMolfile().
 *   drawMode      molecule(s), possibly with [*] attachment points, as V2000; read with
 *                 getDrawnMolfile().
 * In any mode, onMolChange (optional) receives the structure, in that mode's format, after
 * every edit.
 *
 * hideAtomMaps (optional): on Ketcher 3, draw no atom-map numbers and hide the mapping
 * tools; the structure, and every format read from it, keeps the maps. The 2020 bundle
 * never draws them, in any mode.
 */

import React from 'react';
import { IndigoWs } from './WebServices';
import { Loader } from './Loader';
import { molfileDummyAtomFeatures } from './util/mol_format_utils';

/** The 2020 bundle's tool suppression, per mode. */
const LEGACY_DISABLED_TOOLS = {
    default: 'process,rgroup,rgroup-data,sgroup,sgroup-data,reaction,chiral-flag',
    reaction: 'process,sgroup,sgroup-data,chiral-flag',
    draw: 'process,sgroup,sgroup-data,reaction,chiral-flag',
};

const KETCHER_PATH = 'ketcher';

// The discriminator must be index.html, not ketcher.html: the 2020 bundles ship only
// ketcher.html, and the Ketcher 3 build emits both so it can take over this path without
// any consumer URL changing.
const KETCHER3_PROBE = `${KETCHER_PATH}/index.html`;
// A path that cannot exist. If the server answers 200 for THIS too, its 200s mean nothing
// — see ketcher3Deployed.
const KETCHER3_CONTROL = `${KETCHER_PATH}/__ketcher3_probe__`;
const PROBE_TIMEOUT_MS = 5000;
const KETCHER3_SRC = `${KETCHER3_PROBE}?provider=server&api_path=/services/indigo/`;

/**
 * What each mode hides on Ketcher 3, named as the host page's TOOLS names them
 * (vendor/ketcher/src/main.jsx). Only reaction mode (bazaar's reaction enumeration) needs the
 * reaction and R-group tools, and only the Compound Editor hides query atoms and bonds:
 * Molexar's * attachment point is one, from the Extended Table. Unlike the 2020 lists,
 * Process (Layout, Clean, Calculated Values, ...) stays, except the 3D Viewer.
 */
const KETCHER3_DISABLED_TOOLS = {
    default: 'reaction,rgroup,sgroup,enhanced-stereo,query,annotations,miew,macromolecules',
    reaction: 'sgroup,enhanced-stereo,annotations,miew,macromolecules',
    draw: 'reaction,rgroup,sgroup,enhanced-stereo,annotations,miew,macromolecules',
};

/** With `hideAtomMaps`, the map numbers and the tools that change them are not shown. */
function ketcher3Src(props) {
    const tools = [KETCHER3_DISABLED_TOOLS[resolveMode(props)]];
    let src = KETCHER3_SRC;
    if (props.hideAtomMaps) {
        src += '&hideAtomMaps=1';
        tools.push('reaction-automap,reaction-map,reaction-unmap');
    }
    return `${src}&disabledTools=${tools.join(',')}`;
}

// Over the top of the canvas, below either generation's toolbar, and transparent to clicks.
const NOTE_STYLE = {
    position: 'absolute',
    top: '48px',
    left: '50%',
    transform: 'translateX(-50%)',
    maxWidth: '70%',
    padding: '2px 8px',
    borderRadius: '4px',
    background: 'rgba(255, 255, 255, 0.9)',
    color: '#8a4b00',
    fontSize: '0.875rem',
    fontStyle: 'italic',
    textAlign: 'center',
    pointerEvents: 'none',
};

/** 'reaction', 'draw' or 'default'. Both flags set resolves to 'reaction'. */
function resolveMode({ reactionMode, drawMode } = {}) {
    if (reactionMode) return 'reaction';
    if (drawMode) return 'draw';
    return 'default';
}

function legacySrc(props) {
    const tools = LEGACY_DISABLED_TOOLS[resolveMode(props)];
    return `${KETCHER_PATH}/ketcher.html?api_path=/services/indigo/&disabledTools=${tools}`;
}

// V2000's counts line is three characters wide, and it has no way to express an AND/OR
// stereo group. `abs` is the Ketcher 3 stereo label that does NOT need V3000; compared as a
// literal because proto does not depend on ketcher-core and so cannot import StereoLabel.
const V2000_MAX_ATOMS = 999;
const ABSOLUTE_STEREO = 'abs';

/** Generous: the standalone WASM build's first open takes a few seconds. */
const READY_TIMEOUT_MS = 30000;
const READY_POLL_MS = 100;

function unsupportedMessage(err) {
    if (/reaction arrow/i.test(err && err.message)) {
        return 'Reactions cannot be submitted as a compound. Remove the reaction arrow to continue.';
    }
    return `This structure cannot be submitted: ${err && err.message}`;
}

/** Ketcher writes these happily, but BMaps cannot build a compound from them. */
function dummyAtomMessage(molText) {
    const features = molfileDummyAtomFeatures(molText);
    if (features.length === 0) return null;
    const list = features.length === 1
        ? features[0]
        : `${features.slice(0, -1).join(', ')} and ${features[features.length - 1]}`;
    return `BMaps cannot load ${list}. Remove them to continue.`;
}

export class Ketcher extends React.Component {
    /**
     * A HEAD rather than loading it and seeing, because an iframe pointed at a 404 still
     * fires `load` and would cost the whole readiness timeout.
     *
     * The second request is a control. SPA-style hosting — an S3 website error document,
     * or CloudFront custom error responses mapping 404 to /index.html with status 200 —
     * answers 200 for paths that do not exist, which would select Ketcher 3 everywhere.
     * So a host that also says 200 to a path that cannot exist is telling us nothing, and
     * the safe reading is "not deployed".
     *
     * `no-store` because a 404 cached before a deploy would keep selecting the 2020
     * bundle, and a 200 cached before a rollback would keep selecting Ketcher 3. The
     * timeout matters more than it looks: without it a hung connection never rejects, so
     * `src` would stay undefined and render() would show NO iframe at all, indefinitely.
     * Anything but a clean answer — timeout, network error, ambiguous host — falls back to
     * the bundle every environment has.
     */
    static async ketcher3Deployed() {
        const head = (path) => fetch(path, {
            method: 'HEAD',
            cache: 'no-store',
            signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
        });
        try {
            const [found, control] = await Promise.all([
                head(KETCHER3_PROBE), head(KETCHER3_CONTROL),
            ]);
            return found.ok && !control.ok;
        } catch (err) {
            return false;
        }
    }

    constructor(props) {
        super(props);
        this.state = {};

        this.setup = this.setup.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.iframeRef = React.createRef();
        this.noteRef = React.createRef();
        this.noteText = '';
        // The note the error replaced, restored when the error clears; null while none.
        this.noteBeforeError = null;
        this.unmounted = false;
    }

    async componentDidMount() {
        // Resolved before the iframe renders, not swapped afterwards: changing src
        // reloads the document we hold a reference and a subscription into.
        const ketcher3 = await Ketcher.ketcher3Deployed();
        this.setState({ src: ketcher3 ? ketcher3Src(this.props) : legacySrc(this.props) });
    }

    componentWillUnmount() {
        // Set first: the probe and the readiness poll can still be in flight, and both
        // check this rather than touching refs that are about to be null.
        this.unmounted = true;
        // Guarded: a throw here reaches the error boundary and takes the app down.
        try {
            this.unsubscribeFromChange();
        } catch (err) {
            console.error(`Error releasing the sketcher change subscription: ${err}`);
        }
    }

    async handleChange() {
        const { onMolChange, onMolError } = this.props;

        if (this.ketcher) {
            const mode = resolveMode(this.props);
            // Only the Compound Editor refuses what BMaps cannot load: the other modes' callers
            // take query features as input, e.g. Molexar's * attachment points.
            if (mode !== 'default') {
                onMolChange(mode === 'reaction'
                    ? await this.getReactionMolfile()
                    : await this.getDrawnMolfile());
                return;
            }
            let data;
            let message;
            try {
                data = await this.molfileFromKetcher();
                message = dummyAtomMessage(data);
            } catch (err) {
                // Ketcher 3 refuses to write a molfile for some drawings, e.g. a reaction.
                message = unsupportedMessage(err);
            }
            if (message) {
                // Say so, rather than letting the last good molecule be submitted, or this
                // one be dropped, silently.
                if (typeof this.noteBeforeError !== 'string') this.noteBeforeError = this.noteText || '';
                this.updateNote(message);
                if (onMolError) onMolError(message);
                return;
            }
            if (typeof this.noteBeforeError === 'string') {
                this.updateNote(this.noteBeforeError);
                this.noteBeforeError = null;
                if (onMolError) onMolError(null);
            }
            try {
                data = await this.serverViewWS(data);
                // // Rdkit implementation was removing chiral information so disable for now
                // const rdkitData = await this.serverViewRDKit(data);
                // if (rdkitData) data = rdkitData;
                // else data = await this.serverViewWS(data);
            } catch (err) {
                // Ignore error (will send coords without Hs to server)
            }
            onMolChange(data);
        }
    }

    async setup() {
        let ketcher;
        try {
            ketcher = await this.waitForKetcher();
        } catch (err) {
            console.error(`Sketcher failed to load: ${err}`);
            this.updateNote('2D sketcher is unavailable');
            return;
        }
        if (this.unmounted) return;
        const { startingMolText, onMolChange } = this.props;
        // From the editor we were handed, not from the src we chose, so a half-finished
        // deploy behaves according to what is actually there.
        this.isModernKetcher = typeof ketcher.editor.subscribe === 'function';
        this.updateOptions(ketcher);
        this.ketcher = ketcher;

        if (startingMolText) {
            let molText = startingMolText;
            try {
                molText = await this.layout(startingMolText);
                this.updateNote('');
            } catch (err) {
                console.error(`Exception in layout: ${err}`);
                this.updateNote('2D layout is unavailable, using flattened 3D view instead');
            }
            if (this.unmounted) return;

            // A Promise on 3.x, undefined on 2020; awaiting suits both and surfaces a
            // failure here rather than as an empty canvas.
            await ketcher.setMolecule(molText);
        }
        // Only for a listener: tracking edits costs work on every stroke, an Indigo round
        // trip in the default mode.
        if (onMolChange) {
            this.changeSubscription = this.subscribeToChange(ketcher);
        }
    }

    /**
     * RXN of the drawn reaction, or '' if there is none or 3.x cannot write it. 3.x's
     * getMolfile refuses a reaction arrow; its getRxn throws without one, and rejects some
     * it cannot express in V2000. Callers treat '' as "nothing usable drawn".
     */
    async getReactionMolfile() {
        if (!this.ketcher) return '';
        if (!this.isModernKetcher) return this.ketcher.getMolfile();
        if (!this.ketcher.containsReaction()) return '';
        try {
            return await this.ketcher.getRxn('v2000');
        } catch (err) {
            console.error(`Could not write the reaction as RXN: ${err}`);
            return '';
        }
    }

    /**
     * V2000 molfile of the drawn structure(s), which the draw-mode caller requires, or ''
     * if 3.x cannot write one: a reaction arrow, or a structure V2000 cannot hold.
     */
    async getDrawnMolfile() {
        if (!this.ketcher) return '';
        if (!this.isModernKetcher) return this.ketcher.getMolfile();
        if (this.ketcher.containsReaction()) return '';
        try {
            return await this.ketcher.getMolfile('v2000');
        } catch (err) {
            console.error(`Could not write the drawing as a V2000 molfile: ${err}`);
            return '';
        }
    }

    /**
     * Does this structure need V3000 to be written without loss?
     *
     * Only two things make it necessary: more than 999 atoms, and enhanced stereochemistry
     * — an AND or OR stereo group, which says "racemic here" or "one of these, unknown
     * which". Both are unrepresentable in V2000. Absolute stereo is NOT one of them and
     * must not trigger this, which is why the test is "a label that is not `abs`" rather
     * than "has a label".
     *
     * Read from the editor's own Struct, so it costs no network call.
     */
    needsV3000() {
        try {
            const { atoms } = this.ketcher.editor.struct();
            if (atoms.size > V2000_MAX_ATOMS) return true;
            return [...atoms.values()].some(
                (atom) => atom.stereoLabel && atom.stereoLabel !== ABSOLUTE_STEREO
            );
        } catch (err) {
            // Never block an edit over a format optimisation.
            console.warn(`Could not inspect the structure for V3000 features: ${err}`);
            return false;
        }
    }

    /**
     * The narrowest molfile format that loses nothing: V2000 unless the structure needs
     * V3000, which costs a round trip through the Indigo server's `convert` because Ketcher
     * has no client-side V3000 writer. Falls back to V2000 if the server cannot convert.
     */
    async molfileFromKetcher() {
        // The 2020 bundle is called exactly as before; it has no molAuto concept and no
        // V3000 writer, so there is nothing to choose.
        if (!this.isModernKetcher) return this.ketcher.getMolfile();
        if (!this.needsV3000()) return this.ketcher.getMolfile('v2000');
        try {
            return await this.ketcher.getMolfile('v3000');
        } catch (err) {
            console.warn(`V3000 unavailable (${err}); falling back to V2000, which cannot `
                + 'carry enhanced stereochemistry');
            return this.ketcher.getMolfile('v2000');
        }
    }

    /**
     * Neither generation is ready when the iframe's `load` fires, and `ketcher` can exist
     * before `ketcher.editor`. Waiting for `ketcher.editor` covers both.
     */
    async waitForKetcher() {
        const deadline = Date.now() + READY_TIMEOUT_MS;
        for (;;) {
            const frame = this.iframeRef.current && this.iframeRef.current.contentWindow;
            // eslint-disable-next-line no-underscore-dangle
            const hostError = frame && frame.__ketcherHostError;
            if (hostError) throw new Error(`host refused to start: ${hostError}`);
            if (frame && frame.ketcher && frame.ketcher.editor) return frame.ketcher;
            if (this.unmounted) throw new Error('sketcher closed before it was ready');
            if (Date.now() > deadline) {
                throw new Error(`not ready within ${READY_TIMEOUT_MS} ms`);
            }
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, READY_POLL_MS));
        }
    }

    /**
     *   2.0.0-RC  editor.on(evt, handler)        / editor.off(evt, handler)
     *   3.18      editor.subscribe(evt, handler) / editor.unsubscribe(evt, subscriber)
     *
     * 3.18's unsubscribe takes the SUBSCRIBER subscribe returned, not the handler — which
     * is why it is stored. Passing the handler neither throws nor unsubscribes.
     *
     * @returns the subscription, or null if the dialog closed while setup() was still
     *   running. A second caller would need to handle that null.
     */
    subscribeToChange(ketcher) {
        // setup() can still be running when the dialog closes — `layout` is an Indigo
        // round trip. componentWillUnmount will already have run with changeSubscription
        // null, so a subscription attached now is attached to a detached iframe's editor
        // and never released. Guarded here rather than at the call site because this is
        // the only place the consequence is a leak.
        if (this.unmounted) return null;
        const { editor } = ketcher;
        if (typeof editor.subscribe === 'function') {
            return { kind: 'subscriber', value: editor.subscribe('change', this.handleChange) };
        }
        editor.on('change', this.handleChange);
        return { kind: 'handler', value: this.handleChange };
    }

    unsubscribeFromChange() {
        if (!this.ketcher || !this.changeSubscription) return;
        const { editor } = this.ketcher;
        const { kind, value } = this.changeSubscription;
        this.changeSubscription = null;
        if (kind === 'subscriber') {
            editor.unsubscribe('change', value);
        } else if (typeof editor.off === 'function') {
            editor.off('change', value);
        }
    }

    // I was not able to figure out how to specify inital settings for Ketcher,
    // so we need to update them after it is created.  Unfortunately, this does
    // not update the settings UI.
    //
    // Unguarded on purpose: 3.18 still has editor.options(), so this does not throw there.
    // `hideChiralFlag` is simply unknown to 3.18, making it a no-op.
    updateOptions(ketcher) {
        const options = ketcher.editor.options();
        options.hideChiralFlag = true;
        ketcher.editor.options(options);
    }

    async serverViewWS(data) {
        return IndigoWs.toServerView(data);
    }

    async serverViewRDKit(data) {
        if (!Loader.RDKitExport) return '';
        const molA = await Loader.RDKitExport.getMol(data);
        const molB = await Loader.RDKitExport.getMol(molA.add_hs());
        const s = molB.get_new_coords(true);
        molA.delete();
        molB.delete();
        return s;
    }

    async layout(molText) {
        return this.layoutWS(molText);
        // // Rdkit implementation was removing chiral information so disable for now
        // let s = await this.layoutRDKit(molText);
        // if (!s) {
        //     s = await this.layoutWS(molText);
        // }
        // return s;
    }

    async layoutWS(molText) {
        return IndigoWs.toSketcherView(molText);
    }

    async layoutRDKit(molText) {
        if (!Loader.RDKitExport) return '';
        const jsMol = Loader.RDKitExport.getCleanMol(molText);
        const s = jsMol.get_molblock();
        jsMol.delete();

        return s;
    }

    updateNote(text) {
        this.noteText = text;
        // Reachable after unmount: setup() reports failures this way, and it can still be
        // awaiting the editor when the dialog closes.
        if (this.noteRef.current) {
            this.noteRef.current.innerText = text;
            this.noteRef.current.hidden = !text;
        }
    }

    render() {
        const { iframeId } = this.props;
        const { src } = this.state;
        const iframeStyle = { height: '99%', width: '100%', border: '1px solid darkgray' };

        return (
            <div style={{ position: 'relative', height: '100%' }}>
                <span ref={this.noteRef} style={NOTE_STYLE} hidden />
                {src && (
                    <iframe
                        id={iframeId}
                        title="Ketcher, a 2D Sketcher"
                        // Chosen in componentDidMount; see setup() for how it is waited on
                        // and updateOptions() for Ketcher settings.
                        src={src}
                        style={iframeStyle}
                        ref={this.iframeRef}
                        onLoad={this.setup}
                    />
                )}
            </div>
        );
    }
}
